import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Mail,
  Download,
  Search,
  RefreshCw,
  AlertCircle,
  Loader2,
  ChevronDown,
  Check,
  Eye,
  X,
  Phone,
  Send,
} from 'lucide-react';
import { api } from '../../services/api';
import EmptyState from '../../components/EmptyState';

export default function Emails() {
  const { id } = useParams();
  const [emails, setEmails] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0 });

  const statusOptions = [
    { value: '', label: 'All Emails' },
    { value: 'sent', label: 'Sent' },
    { value: 'failed', label: 'Failed' },
  ];

  const fetchEmails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getEmails(id, {
        search: searchQuery,
        status: statusFilter,
      });
      setEmails(response.emails || []);
    } catch (err) {
      console.error('Failed to fetch emails:', err);
      // Treat 404 as "no data" - endpoint might not be implemented yet
      const is404 = err.message?.includes('404') || err.message?.includes('Not Found') || err.status === 404;
      if (is404) {
        setEmails([]);
      } else {
        setError('Failed to fetch');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, searchQuery, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.getEmailStats(id);
      setStats(response.stats || { total: 0, sent: 0, failed: 0 });
    } catch (err) {
      // Treat 404 as "no data" - just show zeros
      console.error('Failed to fetch email stats:', err);
      setStats({ total: 0, sent: 0, failed: 0 });
    }
  }, [id]);

  useEffect(() => {
    fetchEmails();
    fetchStats();
  }, [id, statusFilter]);

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    if (searchQuery !== undefined) {
      fetchEmails();
    }
  }, [searchQuery]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      pending: 'bg-amber-100 text-amber-700',
    };
    const labels = {
      sent: 'Sent',
      failed: 'Failed',
      pending: 'Pending',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setShowViewModal(true);
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const exportCSV = () => {
    const headers = ['Recipient Email', 'Recipient Name', 'Subject', 'Status', 'Sent At', 'Customer Phone'];
    const rows = emails.map(email => [
      email.recipientEmail || '',
      email.recipientName || '',
      email.subject || '',
      email.status || '',
      email.sentAt ? new Date(email.sentAt).toLocaleString() : '',
      email.customerPhone ? `'+${email.customerPhone.replace(/^\+/, '')}` : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `emails-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Show full-page loading spinner for initial load
  if (isLoading && emails.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Emails</h1>
            <p className="text-text-secondary">
              {stats.sent} sent, {stats.failed} failed
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => { fetchEmails(); fetchStats(); }}
            disabled={isLoading}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 text-text-muted ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={exportCSV}
            disabled={emails.length === 0}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
              <p className="text-sm text-text-muted">Total Emails</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <Send className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.sent}</p>
              <p className="text-sm text-text-muted">Sent Successfully</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
              <p className="text-sm text-text-muted">Failed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {emails.length === 0 && !searchQuery && !statusFilter ? (
        <EmptyState
          icon={Mail}
          title="No emails sent yet"
          description="When your AI assistant sends emails on your behalf, they'll appear here."
        />
      ) : (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl border border-border p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="text"
                    placeholder="Search by email or subject..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="input-field pl-10"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>

              {/* Status Filter */}
              <div className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-border rounded-lg
                             hover:border-primary/30 transition-colors text-sm"
                >
                  <span className="font-medium text-text-primary">
                    {statusOptions.find(opt => opt.value === statusFilter)?.label}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-text-muted transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showStatusDropdown && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-elevated border border-border z-50 overflow-hidden animate-slide-up">
                    <div className="p-2">
                      {statusOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setStatusFilter(option.value);
                            setShowStatusDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left rounded-lg text-sm flex items-center justify-between
                                     hover:bg-gray-50 transition-colors
                                     ${statusFilter === option.value ? 'bg-primary/5 text-primary' : 'text-text-primary'}`}
                        >
                          <span>{option.label}</span>
                          {statusFilter === option.value && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Emails Table */}
          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-primary/5 to-accent/5 border-b border-border">
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Recipient</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Subject</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Sent At</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {emails.map((email, index) => (
                    <tr
                      key={email.id}
                      className="hover:bg-gray-50 transition-colors animate-slide-up"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      {/* Recipient */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-text-primary">
                              {email.recipientEmail || 'Unknown'}
                            </p>
                            {email.recipientName && (
                              <p className="text-sm text-text-muted">{email.recipientName}</p>
                            )}
                            {email.customerPhone && (
                              <p className="text-xs text-text-muted flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {email.customerPhone}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Subject */}
                      <td className="px-6 py-4">
                        <p className="text-sm text-text-primary font-medium">
                          {truncateText(email.subject, 40)}
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          {truncateText(email.body, 60)}
                        </p>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        {getStatusBadge(email.status)}
                        {email.errorMessage && (
                          <p className="text-xs text-red-500 mt-1" title={email.errorMessage}>
                            {truncateText(email.errorMessage, 30)}
                          </p>
                        )}
                      </td>

                      {/* Sent At */}
                      <td className="px-6 py-4">
                        <span className="text-sm text-text-secondary">
                          {formatDateTime(email.sentAt)}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleViewEmail(email)}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          title="View Email"
                        >
                          <Eye className="w-4 h-4 text-text-muted" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {emails.length === 0 && (searchQuery || statusFilter) && (
              <div className="text-center py-12">
                <p className="text-text-muted">No emails matching your filters</p>
              </div>
            )}
          </div>

          {/* Results count */}
          {emails.length > 0 && (
            <div className="mt-4 text-sm text-text-muted text-center">
              Showing {emails.length} email{emails.length !== 1 ? 's' : ''}
            </div>
          )}
        </>
      )}

      {/* View Email Modal */}
      {showViewModal && selectedEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-up">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-lg font-semibold text-text-primary">Email Details</h2>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedEmail.status)}
                  <span className="text-sm text-text-muted">
                    {formatDateTime(selectedEmail.sentAt)}
                  </span>
                </div>

                {/* To */}
                <div>
                  <label className="text-sm font-medium text-text-secondary">To</label>
                  <p className="text-text-primary mt-1">
                    {selectedEmail.recipientName && `${selectedEmail.recipientName} `}
                    &lt;{selectedEmail.recipientEmail}&gt;
                  </p>
                </div>

                {/* Subject */}
                <div>
                  <label className="text-sm font-medium text-text-secondary">Subject</label>
                  <p className="text-text-primary mt-1 font-medium">{selectedEmail.subject}</p>
                </div>

                {/* Body */}
                <div>
                  <label className="text-sm font-medium text-text-secondary">Body</label>
                  <div className="mt-2 p-4 bg-gray-50 rounded-xl">
                    <pre className="text-sm text-text-primary whitespace-pre-wrap font-sans">
                      {selectedEmail.body}
                    </pre>
                  </div>
                </div>

                {/* Error Message */}
                {selectedEmail.errorMessage && (
                  <div>
                    <label className="text-sm font-medium text-red-600">Error</label>
                    <p className="mt-1 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      {selectedEmail.errorMessage}
                    </p>
                  </div>
                )}

                {/* Customer Phone */}
                {selectedEmail.customerPhone && (
                  <div>
                    <label className="text-sm font-medium text-text-secondary">Customer Phone</label>
                    <p className="text-text-primary mt-1 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-text-muted" />
                      {selectedEmail.customerPhone}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-border flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
